// resolveCitations — deep module behind <VariantTabsWithCitations>.
//
// A composer draft body carries inline `[^N]` markers (markdown footnote
// syntax). `resolveCitations` splits a body into an ordered list of
// segments — plain text and citation references — and pairs each marker
// with its `ComposerCitation`. The UI renders text segments as prose and
// citation segments as clickable superscripts that scroll to a footnote
// table at the bottom of the variant.
//
// Rules:
//   - `[^N]` where N matches a citation's `marker` → resolved reference.
//     Markers are compared as strings ('1', 'a') — the ontology
//     `ComposerCitationSchema.marker` is `z.string()`.
//   - `[^N]` with no matching citation → kept as a reference segment but
//     flagged `missing: true`; the UI renders it greyed without a link.
//   - The returned `footnotes` list is every citation actually referenced
//     by the body, in first-appearance order — duplicates collapse.

import type { ComposerCitation } from '@/data/types';

/** A run of plain prose between citation markers. */
export interface CitationTextSegment {
    kind: 'text';
    text: string;
}

/** A resolved (or unresolved) `[^N]` citation marker in the body. */
export interface CitationRefSegment {
    kind: 'citation';
    /** The marker token from `[^N]` (string — '1', 'a'). */
    marker: string;
    /** The matched citation, or null when no citation carries this marker. */
    citation: ComposerCitation | null;
    /** True when `[^N]` has no matching citation entry. */
    missing: boolean;
    /**
     * 1-based footnote index — the position of this citation in the
     * de-duplicated `footnotes` list. Lets the UI render `[1]`, `[2]`, …
     * sequentially even when body markers are out of order or sparse.
     */
    footnoteIndex: number;
}

export type CitationSegment = CitationTextSegment | CitationRefSegment;

/** One entry in the footnote table rendered at the bottom of a variant. */
export interface ResolvedFootnote {
    /** 1-based index shown in the footnote table and the superscript. */
    index: number;
    /** The original `[^N]` marker token. */
    marker: string;
    citation: ComposerCitation | null;
    missing: boolean;
}

export interface ResolveCitationsResult {
    /** Body split into ordered prose / citation segments. */
    segments: CitationSegment[];
    /** De-duplicated, first-appearance-ordered footnote table. */
    footnotes: ResolvedFootnote[];
}

// Matches a markdown footnote marker: `[^1]`, `[^42]`, `[^a]`. Capturing
// group 1 is the marker token (alphanumeric — the ontology marker is a
// string). Global so we can walk every occurrence.
const MARKER_RE = /\[\^([A-Za-z0-9]+)\]/g;

/**
 * Resolve `[^N]` markers in `body` against `citations`.
 *
 * Pure and deterministic — no side effects, safe in render paths.
 */
export function resolveCitations(
    body: string,
    citations: readonly ComposerCitation[] = [],
): ResolveCitationsResult {
    const byMarker = new Map<string, ComposerCitation>();
    for (const c of citations) byMarker.set(String(c.marker), c);

    const segments: CitationSegment[] = [];
    // Footnote index is assigned on first appearance of each marker.
    const footnoteIndexByMarker = new Map<string, number>();
    const footnotes: ResolvedFootnote[] = [];

    let lastIndex = 0;
    // Reset lastIndex on the shared regex before walking.
    MARKER_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = MARKER_RE.exec(body)) !== null) {
        const [raw, numStr] = match;
        const marker = numStr;

        // Prose preceding this marker.
        if (match.index > lastIndex) {
            segments.push({ kind: 'text', text: body.slice(lastIndex, match.index) });
        }
        lastIndex = match.index + raw.length;

        const citation = byMarker.get(marker) ?? null;
        const missing = citation === null;

        let footnoteIndex = footnoteIndexByMarker.get(marker);
        if (footnoteIndex === undefined) {
            footnoteIndex = footnotes.length + 1;
            footnoteIndexByMarker.set(marker, footnoteIndex);
            footnotes.push({ index: footnoteIndex, marker, citation, missing });
        }

        segments.push({ kind: 'citation', marker, citation, missing, footnoteIndex });
    }

    // Trailing prose after the final marker.
    if (lastIndex < body.length) {
        segments.push({ kind: 'text', text: body.slice(lastIndex) });
    }
    // Body with no markers at all — single text segment.
    if (segments.length === 0) {
        segments.push({ kind: 'text', text: body });
    }

    return { segments, footnotes };
}
